import { useState } from 'react';
import Button from '@/shared/components/Button';
import { _t } from '@/i18n/t';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/dialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (_open: boolean) => void;
  onConfirm: () => Promise<void> | void;
  title?: string;
  description?: string;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger';
  cancelLabel?: string;
  elevated?: boolean;
  hideClose?: boolean;
}

const ConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmLabel,
  confirmVariant = 'danger',
  cancelLabel,
  elevated = false,
  hideClose = false,
}: ConfirmDialogProps) => {
  const resolvedTitle = title || _t('confirm.defaultTitle');
  const resolvedDescription = description || _t('confirm.defaultDescription');
  const resolvedConfirmLabel = confirmLabel || _t('confirm.defaultConfirm');
  const resolvedCancelLabel = cancelLabel || _t('kb.actions.cancel');
  const [isConfirming, setIsConfirming] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="confirm-dialog"
        hideClose={hideClose}
        className={`w-full max-w-confirm-max-w rounded-lg bg-card p-0 shadow-xl${elevated ? ' z-[1000]' : ''}`}
        overlayClassName={elevated ? 'z-[1000]' : undefined}
      >
        <DialogHeader className="px-6 pb-2 pt-6">
          <DialogTitle>{resolvedTitle}</DialogTitle>
          <DialogDescription className="mt-2 break-words">{resolvedDescription}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="px-6 pb-6 pt-4">
          <Button
            variant="secondary"
            data-testid="confirm-dialog-cancel-btn"
            onClick={() => onOpenChange(false)}
          >
            {resolvedCancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            data-testid="confirm-dialog-confirm-btn"
            disabled={isConfirming}
            onClick={async () => {
              setIsConfirming(true);
              try {
                await onConfirm();
                onOpenChange(false);
              } catch {
                setIsConfirming(false);
                return;
              }
              setIsConfirming(false);
            }}
          >
            {resolvedConfirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;